<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div v-if="isEditing" class="editable-numeric-cell-content-wrapper">
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content text-right"
                    :style="{ borderRight: 'none', borderRadius: '4px 0 0 4px' }"
                    contenteditable="true"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                    @wheel.prevent="handleWheel"
                    @paste="handlePaste"
                >
                    {{ value }}
                </div>

                <!-- Botones de spin -->
                <div
                    class="editable-numeric-cell-spin-buttons"
                    :class="{
                        'editable-numeric-cell-spin-invalid': invalid,
                        'editable-numeric-cell-spin-editing': !invalid
                    }"
                >
                    <button type="button" tabindex="-1" @mousedown.prevent="spinUp">▲</button>
                    <button type="button" tabindex="-1" @mousedown.prevent="spinDown">▼</button>
                </div>
            </div>

            <!-- Display mode -->
            <span
                v-else
                class="md-editable-grid-cell-display text-right"
                :class="{ 'md-editable-grid-cell-non-editable': !editable }"
                @dblclick="requestEdit"
                @keydown="handleDisplayKeydown"
                tabindex="0"
                ref="displayRef"
            >
                {{ valueFormatted }}
            </span>
        </div>
    </td>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import EditableCellBase from './EditableCellBase.vue';
import Utils from '@/common/utils.js';
import validationRules from '@/common/validationRules';

export default {
    name: 'EditableNumericCell',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    props: {
        value: [String, Number],
        inputType: {
            type: String,
            default: 'integer',
            validator: v => ['integer', 'decimal'].includes(v)
        }, //Options 'integer' or 'decimal'
        step: { type: Number, default: 1 },
        min: { type: Number, default: null },
        max: { type: Number, default: null },
        round: { type: Number, default: 2 },
        allowNegativeValue: { type: Boolean, default: false }
    },
    data() {
        return {};
    },
    methods: {
        startEdit(char) {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            if (char !== undefined) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.inputContentFocus();
            }
        },
        finishEdit(event) {
            if (this.isEditing) {
                const val = event.target.innerText.trim();
                const changed = val !== String(this.value).trim();

                let current = null;
                if (!Utils.isNullOrEmptyString(val)) {
                    const num = parseFloat(val) || 0;

                    if (this.inputType === 'decimal') {
                        const decimals = this.round != null ? this.round : 2;
                        current = parseFloat(num.toFixed(decimals));
                    } else {
                        current = Math.round(num);
                    }
                }

                const valid = this.validateCell(current);
                this.isEditing = false;

                if (changed) {
                    if (this.isAppendSection) {
                        this.$emit('input', current);
                    } else {
                        this.$parentInstance.cellEditComplete(this.createEventData(current));
                    }
                }

                if (!valid) {
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.invalidMessage(this.name),
                        'errorAutoHide'
                    );
                }
            }
        },

        async handleKeydown(event) {
            const isMac = navigator.platform.toUpperCase().includes('MAC');
            const isPasteCombo =
                (!isMac && event.ctrlKey && event.key.toLowerCase() === 'v') ||
                (isMac && event.metaKey && event.key.toLowerCase() === 'v');

            if (isPasteCombo) {
                event.preventDefault();
                let paste = '';
                try {
                    paste = await navigator.clipboard.readText();
                } catch (e) {
                    //
                }
                this.processPaste(paste);
                return;
            }

            if (/^[0-9]$/.test(event.key)) {
                return;
            }

            if (event.key === '-' && this.allowNegativeValue) {
                event.preventDefault();
                this.toggleValue();
                return;
            }

            if (
                this.inputType === 'decimal' &&
                (event.key === ',' || event.key === '.') &&
                !this.containDecimalSeparator()
            ) {
                return;
            }

            const allowedKeys = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Tab',
                'Enter',
                'Escape'
            ];
            if (allowedKeys.includes(event.key)) {
                switch (event.key) {
                    case 'Enter':
                        event.preventDefault();
                        this.finishEdit(event);
                        this.$emit('nextCell');
                        break;
                    case 'Tab':
                        event.preventDefault();
                        this.finishEdit(event);
                        this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
                        break;
                    case 'ArrowRight':
                        if (this.caretAtEnd() && !this.isAllSelected()) {
                            event.preventDefault();
                            this.finishEdit(event);
                            this.$emit('nextCell');
                        }
                        break;
                    case 'ArrowLeft':
                        if (this.caretAtStart() && !this.isAllSelected()) {
                            event.preventDefault();
                            this.finishEdit(event);
                            this.$emit('prevCell');
                        }
                        break;
                    case 'ArrowUp':
                        event.preventDefault();

                        if (event.altKey) {
                            this.spinUp();
                        } else {
                            this.finishEdit(event);
                            this.$emit('upCell');
                        }
                        break;
                    case 'ArrowDown':
                        event.preventDefault();

                        if (event.altKey) {
                            this.spinDown();
                        } else {
                            this.finishEdit(event);
                            this.$emit('downCell');
                        }
                        break;
                    case 'Escape':
                        event.preventDefault();

                        this.isEditing = false;
                        break;
                }
                return;
            }

            event.preventDefault();
        },

        spinUp() {
            const el = this.$refs.inputRef;
            let current = parseFloat(el.innerText) || 0;
            current += this.step;

            if (this.max !== null && current > this.max) {
                current = this.max;
            }

            if (!this.allowNegativeValue && current < 0) {
                current = 0;
            }

            if (this.inputType === 'decimal') {
                const decimals = this.round != null ? this.round : 2;
                current = parseFloat(current.toFixed(decimals));
            } else {
                current = Math.round(current);
            }

            el.innerText = current;

            this.$nextTick(() => {
                el.focus();
                this.moveCaretToEnd(el);
            });
        },

        spinDown() {
            const el = this.$refs.inputRef;
            let current = parseFloat(el.innerText) || 0;
            current -= this.step;

            if (this.min !== null && current < this.min) {
                current = this.min;
            }

            if (!this.allowNegativeValue && current < 0) {
                current = 0;
            }

            if (this.inputType === 'decimal') {
                const decimals = this.round != null ? this.round : 2;
                current = parseFloat(current.toFixed(decimals));
            } else {
                current = Math.round(current);
            }

            el.innerText = current;

            this.$nextTick(() => {
                el.focus();
                this.moveCaretToEnd(el);
            });
        },
        handlePaste(event) {
            event.preventDefault();
            const paste = (event.clipboardData || window.clipboardData).getData('text');
            this.processPaste(paste);
        },
        processPaste(paste) {
            let clean = paste;

            if (this.inputType === 'integer') {
                clean = clean.replace(/[^0-9]/g, '');

                if (clean !== '') {
                    let num = parseFloat(clean);

                    if (!isNaN(num)) {
                        clean = String(Math.round(num));
                    }
                }
            } else if (this.inputType === 'decimal') {
                clean = clean.replace(/[^0-9.,]/g, '').replace(',', '.');

                if (clean !== '') {
                    let num = parseFloat(clean);

                    if (!isNaN(num)) {
                        const decimals = this.round != null ? this.round : 2;
                        num = parseFloat(num.toFixed(decimals));
                        clean = num.toFixed(decimals);
                    }
                }
            }
            const el = this.$refs.inputRef;
            el.innerText = clean;

            this.$nextTick(() => {
                el.focus();
                this.moveCaretToEnd(el);
            });
        },

        containDecimalSeparator() {
            const el = this.$refs.inputRef;
            return el.innerText.includes('.') || el.innerText.includes(',');
        },

        handleWheel(event) {
            if (this.editableCell && this.isFocused) {
                event.deltaY < 0 ? this.spinUp() : this.spinDown();
            }
        },

        handleDisplayKeydown(event) {
            const isTopNumber = /^\d$/.test(event.key);
            const isNumpadNumber = /^Numpad[0-9]$/.test(event.code);

            if (isTopNumber || isNumpadNumber || (event.key === '-' && this.allowNegativeValue)) {
                if (this.editableCell) {
                    event.preventDefault();
                    this.requestEdit();
                    this.$nextTick(() => this.startEdit(event.key));
                }

                return;
            }

            // Block other printable chars — only digits and minus are valid input
            if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault();
                return;
            }

            return this._superHandleDisplayKeydown(event);
        },

        _superHandleDisplayKeydown(event) {
            return EditableCellBase.methods.handleDisplayKeydown.call(this, event);
        },

        toggleValue() {
            const el = this.$refs.inputRef;
            let current = parseFloat(el.innerText) || 0;

            if (current !== 0) {
                current = -current;
                let clean = '';

                if (this.inputType === 'integer') {
                    clean = String(Math.round(current));
                } else if (this.inputType === 'decimal' && !isNaN(current)) {
                    const decimals = this.round != null ? this.round : 2;
                    current = parseFloat(current.toFixed(decimals));
                    clean = current.toFixed(decimals);
                }

                el.innerText = clean;
            } else {
                if (el.innerText.trim() === '-') {
                    el.innerText = '';
                } else {
                    el.innerText = '-';
                }
            }

            this.$nextTick(() => {
                el.focus();
                this.moveCaretToEnd(el);
            });
        },

        _validationRules() {
            if (this.min !== null || this.max !== null) {
                let validationRulesList = [...this.validatorRules] || [];

                if (this.min !== null) {
                    validationRulesList.push(
                        validationRules.min(
                            this.min,
                            this.$t('Msg.InvalidMinValue', { min: this.min })
                        )
                    );
                }

                if (this.max !== null) {
                    validationRulesList.push(
                        validationRules.max(
                            this.max,
                            this.$t('Msg.InvalidMaxValue', { max: this.max })
                        )
                    );
                }

                return validationRulesList;
            }

            return null;
        }
    }
};
</script>

<style scoped>
.editable-numeric-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
}
.editable-numeric-cell-spin-buttons {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-left: none;
    border-radius: 0 4px 4px 0;
    box-sizing: border-box;
    margin-right: 2px;
}
.editable-numeric-cell-spin-buttons button {
    flex: 1;
    padding: 0;
    margin: 0;
    cursor: pointer;
    background: #f0f0f0;
    border: none;
    font-size: 12px;
}
.editable-numeric-cell-spin-invalid {
    border: 2px solid red;
    border-left: none;
}
.editable-numeric-cell-spin-editing {
    border: 2px solid #007bff;
    border-left: none;
}
tbody.md-editable-grid-body-append .editable-numeric-cell-spin-editing {
    border: none;
    border-bottom: 1px solid #ddd;
}
</style>
